import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Radar, AlertTriangle, TrendingUp, TrendingDown, Minus, Loader2, RefreshCw, Building2, ArrowRight, Clock, Flame } from 'lucide-react';
import AdminLayout from '@/components/AdminLayout';
import api from '@/lib/api';
import { apiErrorMessage } from '@/lib/api';
import { toast } from 'sonner';
import { getDisplayTitle } from '@/lib/displayTitle';

const LEVEL_META = {
    high: { label: 'Alto riesgo', color: '#EF4444', bg: 'rgba(239,68,68,0.10)' },
    medium: { label: 'Riesgo medio', color: '#F59E0B', bg: 'rgba(245,158,11,0.10)' },
    low: { label: 'Bajo riesgo', color: '#10B981', bg: 'rgba(16,185,129,0.10)' },
};

const ACCENT = '#7C6BEF';

export default function AdminRiskRadar() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all');
    const [selected, setSelected] = useState(null);
    const [distintivos, setDistintivos] = useState([]);
    const navigate = useNavigate();

    const load = async () => {
        try {
            const { data } = await api.get('/admin/risk-radar');
            setData(data);
        } catch (e) {
            toast.error(apiErrorMessage(e, 'Error al cargar el radar'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        api.get('/public/distintivos').then((r) => setDistintivos(r.data || [])).catch(() => setDistintivos([]));
    }, []);

    const distLookup = useMemo(() => Object.fromEntries(distintivos.map((d) => [d.id, d])), [distintivos]);

    const filtered = useMemo(() => {
        if (!data?.clients) return [];
        if (filter === 'all') return data.clients;
        return data.clients.filter((c) => c.level === filter);
    }, [data, filter]);

    return (
        <AdminLayout title="Radar de Riesgo">
            <div className="mb-6">
                <div className="flex items-center gap-2 text-xs uppercase tracking-widest font-bold" style={{ color: ACCENT }}>
                    <Radar className="w-4 h-4" />
                    Vista predictiva · La IA prioriza tus próximos incendios antes de que ocurran
                </div>
                <div className="flex items-center justify-between mt-1">
                    <h1 className="text-3xl font-display font-black">Radar de Riesgo</h1>
                    <button onClick={() => { setLoading(true); load(); }}
                        data-testid="risk-radar-refresh"
                        className="inline-flex items-center gap-2 border-2 px-3 py-2 font-bold text-sm hover:bg-black/5 dark:hover:bg-white/5"
                        style={{ borderColor: 'var(--border)' }}>
                        <RefreshCw className="w-4 h-4" /> Actualizar
                    </button>
                </div>
            </div>

            {loading ? (
                <div className="p-16 text-center opacity-60"><Loader2 className="w-8 h-8 animate-spin mx-auto" /></div>
            ) : (
                <>
                    {/* Stat cards */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                        <StatCard label="Alto riesgo" value={data.counts.high} color="#EF4444" bg="rgba(239,68,68,0.10)" Icon={Flame}
                            onClick={() => setFilter('high')} active={filter === 'high'} testid="stat-high" />
                        <StatCard label="Riesgo medio" value={data.counts.medium} color="#F59E0B" bg="rgba(245,158,11,0.10)" Icon={AlertTriangle}
                            onClick={() => setFilter('medium')} active={filter === 'medium'} testid="stat-medium" />
                        <StatCard label="Bajo riesgo" value={data.counts.low} color="#10B981" bg="rgba(16,185,129,0.10)" Icon={TrendingDown}
                            onClick={() => setFilter('low')} active={filter === 'low'} testid="stat-low" />
                        <StatCard label="Total clientes" value={data.counts.total} color={ACCENT} bg="rgba(124,107,239,0.10)" Icon={Building2}
                            onClick={() => setFilter('all')} active={filter === 'all'} testid="stat-all" />
                    </div>

                    {/* Config hint */}
                    <div className="p-3 mb-4 text-xs opacity-70 border-l-4"
                        style={{ background: 'rgba(124,107,239,0.05)', borderColor: ACCENT }}>
                        Umbral actual: <strong>{data.threshold_config?.critical_threshold} hallazgos críticos</strong> en <strong>{data.threshold_config?.window_days} días</strong>.
                        {' '}El score combina: hallazgos recientes (40 pts) · alertas warning (10 pts) · tendencia empeorando (20 pts) · inactividad (15 pts) · umbral superado (15 pts).
                    </div>

                    {/* Clients table */}
                    {filtered.length === 0 ? (
                        <div className="p-16 text-center" style={{ background: 'var(--card-bg)', border: '1px solid var(--border)' }}>
                            <Radar className="w-10 h-10 mx-auto opacity-40 mb-2" />
                            <div className="opacity-70">Sin clientes en esta categoría.</div>
                        </div>
                    ) : (
                        <div className="space-y-3" data-testid="risk-radar-list">
                            {filtered.map((c) => (
                                <RiskRow key={c.client_id} client={c} onSelect={() => setSelected(c)} />
                            ))}
                        </div>
                    )}
                </>
            )}

            {selected && (
                <RiskDetailModal client={selected} distLookup={distLookup} onClose={() => setSelected(null)} onNavigate={(cid) => navigate(`/admin/clients/${cid}`)} />
            )}
        </AdminLayout>
    );
}

function StatCard({ label, value, color, bg, Icon, onClick, active, testid }) {
    return (
        <button onClick={onClick} data-testid={testid}
            className="text-left p-5 border transition-all hover:shadow-lg"
            style={{
                background: active ? bg : 'var(--card-bg)',
                borderColor: active ? color : 'var(--border)',
                borderWidth: active ? '2px' : '1px',
            }}>
            <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] uppercase tracking-widest font-bold opacity-60">{label}</div>
                <div className="w-9 h-9 flex items-center justify-center" style={{ background: bg, color }}>
                    <Icon className="w-5 h-5" />
                </div>
            </div>
            <div className="text-4xl font-display font-black" style={{ color }}>{value}</div>
        </button>
    );
}

function RiskRow({ client, onSelect }) {
    const meta = LEVEL_META[client.level];
    const TrendIcon = client.trend_delta > 0 ? TrendingUp : client.trend_delta < 0 ? TrendingDown : Minus;
    const trendColor = client.trend_delta > 0 ? '#EF4444' : client.trend_delta < 0 ? '#10B981' : '#9CA3AF';

    return (
        <button onClick={onSelect}
            data-testid={`risk-row-${client.client_id}`}
            className="w-full text-left border transition-all hover:shadow-lg p-4 flex items-center gap-4"
            style={{ background: 'var(--card-bg)', borderColor: meta.color, borderLeftWidth: '4px' }}>
            {/* Score circle */}
            <div className="shrink-0 w-16 h-16 flex items-center justify-center rounded-full border-2"
                style={{ borderColor: meta.color, background: meta.bg }}>
                <div className="text-center">
                    <div className="text-lg font-display font-black leading-none" style={{ color: meta.color }}>{client.score}</div>
                    <div className="text-[8px] uppercase tracking-widest font-bold opacity-60">score</div>
                </div>
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                    <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5"
                        style={{ background: meta.bg, color: meta.color }}>
                        {meta.label}
                    </span>
                    {client.threshold_breached && (
                        <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 inline-flex items-center gap-1"
                            style={{ background: 'rgba(239,68,68,0.15)', color: '#EF4444' }}>
                            <AlertTriangle className="w-3 h-3" /> Umbral superado
                        </span>
                    )}
                    {client.industry && (
                        <span className="text-[10px] uppercase tracking-widest font-bold opacity-60">{client.industry}</span>
                    )}
                </div>
                <div className="font-display font-black text-lg truncate">{client.client_name}</div>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs opacity-80 mt-1">
                    <span className="inline-flex items-center gap-1"><Flame className="w-3 h-3" style={{ color: '#EF4444' }} /> <strong>{client.critical_30d}</strong> críticos 30d</span>
                    <span className="inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" style={{ color: '#F59E0B' }} /> <strong>{client.warning_30d}</strong> warning 30d</span>
                    <span className="inline-flex items-center gap-1" style={{ color: trendColor }}>
                        <TrendIcon className="w-3 h-3" /> Tendencia {client.trend_delta > 0 ? `+${client.trend_delta}` : client.trend_delta}
                    </span>
                    <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />
                        {client.days_since_last_inspection == null ? 'Sin auditorías' : `${client.days_since_last_inspection}d desde última`}
                    </span>
                </div>
            </div>

            <ArrowRight className="w-5 h-5 opacity-40 shrink-0" />
        </button>
    );
}

function RiskDetailModal({ client, distLookup, onClose, onNavigate }) {
    const meta = LEVEL_META[client.level];
    const [findings, setFindings] = useState(null);
    const [loadingF, setLoadingF] = useState(true);

    useEffect(() => {
        let ignore = false;
        setLoadingF(true);
        api.get(`/admin/risk-radar/${client.client_id}/findings`)
            .then((r) => { if (!ignore) setFindings(r.data); })
            .catch(() => { if (!ignore) setFindings({ findings: [], critical_count: 0, warning_count: 0 }); })
            .finally(() => { if (!ignore) setLoadingF(false); });
        return () => { ignore = true; };
    }, [client.client_id]);

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ background: 'rgba(0,0,0,0.55)' }} onClick={onClose}
            data-testid="risk-detail-modal">
            <div className="max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col border" style={{ background: 'var(--card-bg)', borderColor: 'var(--border)' }}
                onClick={(e) => e.stopPropagation()}>
                <div className="p-5 border-b flex items-center justify-between shrink-0" style={{ borderColor: 'var(--border)' }}>
                    <div>
                        <div className="text-[10px] uppercase tracking-widest font-bold opacity-60">Detalle de riesgo</div>
                        <div className="font-display font-black text-2xl">{client.client_name}</div>
                    </div>
                    <div className="text-right">
                        <div className="text-4xl font-display font-black" style={{ color: meta.color }}>{client.score}</div>
                        <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: meta.color }}>{meta.label}</div>
                    </div>
                </div>
                <div className="p-5 space-y-4 overflow-y-auto flex-1 min-h-0">
                    <div>
                        <div className="text-[10px] uppercase tracking-widest font-bold opacity-60 mb-2">Composición del score</div>
                        <div className="space-y-2">
                            <ScoreBar label="Hallazgos críticos (últimos 30d)" value={client.breakdown.critical} max={40} color="#EF4444" />
                            <ScoreBar label="Hallazgos warning (últimos 30d)" value={client.breakdown.warning} max={10} color="#F59E0B" />
                            <ScoreBar label="Tendencia empeorando" value={client.breakdown.trend} max={20} color="#DC2626" />
                            <ScoreBar label="Inactividad" value={client.breakdown.inactivity} max={15} color="#6366F1" />
                            <ScoreBar label="Umbral superado" value={client.breakdown.threshold} max={15} color="#B91C1C" />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                        <Kpi label="Críticos 30d" value={client.critical_30d} />
                        <Kpi label="Warning 30d" value={client.warning_30d} />
                        <Kpi label="Δ tendencia" value={client.trend_delta > 0 ? `+${client.trend_delta}` : client.trend_delta} />
                        <Kpi label="Total auditorías" value={client.total_inspections} />
                    </div>

                    {/* Hallazgos IA */}
                    <div>
                        <div className="text-[10px] uppercase tracking-widest font-bold opacity-60 mb-2 flex items-center gap-2">
                            <AlertTriangle className="w-3 h-3" style={{ color: '#EF4444' }} />
                            Hallazgos detectados por IA
                            {findings && (
                                <span className="opacity-80">
                                    ({findings.critical_count} críticos · {findings.warning_count} warning)
                                </span>
                            )}
                        </div>
                        {loadingF ? (
                            <div className="p-8 text-center opacity-60"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
                        ) : !findings?.findings?.length ? (
                            <div className="p-6 text-center text-sm opacity-60 border" style={{ borderColor: 'var(--border)' }}>
                                Sin hallazgos IA registrados para este cliente.
                            </div>
                        ) : (
                            <ul className="space-y-2" data-testid="risk-findings-list">
                                {findings.findings.slice(0, 12).map((f, idx) => (
                                    <FindingItem key={idx} f={f} distLookup={distLookup} onOpen={() => onNavigate(client.client_id, `inspection:${f.inspection_id}`)} />
                                ))}
                                {findings.findings.length > 12 && (
                                    <div className="text-[11px] opacity-60 text-center pt-1">
                                        +{findings.findings.length - 12} hallazgos más — abre el cliente para ver todos.
                                    </div>
                                )}
                            </ul>
                        )}
                    </div>

                    <div className="flex gap-2 pt-2">
                        <button onClick={() => onNavigate(client.client_id)}
                            data-testid="risk-detail-open-client"
                            className="flex-1 text-white px-4 py-2 font-bold inline-flex items-center justify-center gap-2"
                            style={{ background: ACCENT }}>
                            Ver cliente <ArrowRight className="w-4 h-4" />
                        </button>
                        <button onClick={onClose}
                            className="border-2 px-4 py-2 font-bold hover:bg-black/5 dark:hover:bg-white/5"
                            style={{ borderColor: 'var(--border)' }}>
                            Cerrar
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function ScoreBar({ label, value, max, color }) {
    const pct = Math.min(100, (value / max) * 100);
    return (
        <div>
            <div className="flex items-center justify-between text-xs mb-1">
                <span>{label}</span>
                <span className="font-bold" style={{ color }}>{value} / {max}</span>
            </div>
            <div className="w-full h-2 bg-black/10 dark:bg-white/10 overflow-hidden">
                <div style={{ width: `${pct}%`, background: color }} className="h-full transition-all" />
            </div>
        </div>
    );
}

function Kpi({ label, value }) {
    return (
        <div className="p-2 border text-center" style={{ borderColor: 'var(--border)' }}>
            <div className="text-[9px] uppercase tracking-widest font-bold opacity-60">{label}</div>
            <div className="text-xl font-display font-black">{value}</div>
        </div>
    );
}

function timeAgo(iso) {
    if (!iso) return '';
    const min = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
    if (min < 1) return 'ahora';
    if (min < 60) return `hace ${min} min`;
    const h = Math.floor(min / 60);
    if (h < 24) return `hace ${h} h`;
    const d = Math.floor(h / 24);
    return `hace ${d} d`;
}

function FindingItem({ f, distLookup, onOpen }) {
    const isCritical = f.ai_flag === 'critical';
    const color = isCritical ? '#EF4444' : '#F59E0B';
    const bg = isCritical ? 'rgba(239,68,68,0.06)' : 'rgba(245,158,11,0.06)';
    const { title: distTitle } = getDisplayTitle(f, distLookup);
    return (
        <li className="border-l-4 p-3 flex gap-3" style={{ borderColor: color, background: bg }}
            data-testid={`risk-finding-${f.ai_flag}`}>
            {f.photo_url ? (
                <img src={f.photo_url} alt="evidencia"
                    className="w-16 h-16 object-cover shrink-0 border" style={{ borderColor: color }} />
            ) : (
                <div className="w-16 h-16 shrink-0 flex items-center justify-center opacity-30 border" style={{ borderColor: 'var(--border)' }}>
                    <AlertTriangle className="w-6 h-6" />
                </div>
            )}
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5"
                        style={{ background: color, color: 'white' }}>
                        {isCritical ? 'CRÍTICO' : 'REVISAR'}
                    </span>
                    {distTitle && (
                        <span className="text-[10px] uppercase tracking-widest font-bold" style={{ color }}>
                            {distTitle}
                        </span>
                    )}
                    <span className="text-[10px] opacity-50">{timeAgo(f.created_at)}</span>
                </div>
                {f.question_text && (
                    <div className="text-xs font-bold opacity-80 mb-0.5 truncate">{f.question_text}</div>
                )}
                <div className="text-xs opacity-90 line-clamp-2">{f.ai_analysis || 'Sin análisis'}</div>
                {f.ai_legal_framework && (
                    <div className="text-[10px] opacity-60 mt-1 italic truncate">
                        Marco jurídico: {f.ai_legal_framework}
                    </div>
                )}
            </div>
        </li>
    );
}
